import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { Loader2, Shield, Upload, FileText, X, CheckCircle, AlertTriangle, Info, ExternalLink, RefreshCw, ChevronDown } from 'lucide-react';
import API_BASE from '../../utils/apiBase';
import { DBS_CHECK_ROUTE_LINKS } from '../../lib/dbsCheckRoutes';
import { getRTWCheckEditPrefill } from '../../lib/rtwCheckEditPrefill';
import { getDBSCheckEditPrefill } from '../../lib/dbsCheckEditPrefill';
import VerificationEvidenceWorkbench from './VerificationEvidenceWorkbench';

const API = API_BASE;

// ==================== AUDIT-READY CHECK METHODS ====================
// These verification methods reflect QA/inspection expectations
// Values are backend enum-compatible, labels are user-friendly
// Organized by requirement type for requirement-aware dropdowns

const CHECK_METHODS = {
  // Right to Work verification methods - UK GOVERNMENT COMPLIANT
  // Values match VALID_RTW_CHECK_METHODS in backend
  right_to_work: [
    { value: 'home_office_online_check', label: 'Home Office Online Check (Share Code)', recommended: true, route: 'home_office_online_check' },
    { value: 'manual_passport_uk_irish', label: 'Manual Check - UK/Irish Passport', route: 'manual_list_a_check' },
    { value: 'manual_list_a_document', label: 'Manual Check - List A Document', route: 'manual_list_a_check' },
    { value: 'manual_list_b_group_1', label: 'Manual Check - List B Group 1 (Time-Limited)', route: 'manual_list_b_group_1_check' },
    { value: 'manual_list_b_group_2_ecs', label: 'Manual Check - List B Group 2 / ECS', route: 'manual_list_b_group_2_check' },
    { value: 'idsp_check', label: 'Digital Verification Service (IDSP)', route: 'digital_verification_service_check' },
    { value: 'ecs_pvn_check', label: 'Employer Checking Service (PVN)', route: 'ecs_pvn_check' }
  ],
  right_to_work_check: [
    { value: 'home_office_online_check', label: 'Home Office Online Check (Share Code)', recommended: true, route: 'home_office_online_check' },
    { value: 'manual_passport_uk_irish', label: 'Manual Check - UK/Irish Passport', route: 'manual_list_a_check' },
    { value: 'manual_list_a_document', label: 'Manual Check - List A Document', route: 'manual_list_a_check' },
    { value: 'manual_list_b_group_1', label: 'Manual Check - List B Group 1 (Time-Limited)', route: 'manual_list_b_group_1_check' },
    { value: 'manual_list_b_group_2_ecs', label: 'Manual Check - List B Group 2 / ECS', route: 'manual_list_b_group_2_check' },
    { value: 'idsp_check', label: 'Digital Verification Service (IDSP)', route: 'digital_verification_service_check' },
    { value: 'ecs_pvn_check', label: 'Employer Checking Service (PVN)', route: 'ecs_pvn_check' }
  ],
  
  // DBS verification methods
  dbs: [
    { value: 'dbs_certificate_review', label: 'DBS Certificate Review' },
    { value: 'dbs_update_service_check', label: 'DBS Update Service Check' },
    { value: 'other', label: 'Other Documented Verification' }
  ],
  dbs_status_check: [
    { value: 'dbs_certificate_review', label: 'DBS Certificate Review' },
    { value: 'dbs_update_service_check', label: 'DBS Update Service Check' },
    { value: 'other', label: 'Other Documented Verification' }
  ],
  
  // Identity verification methods
  identity: [
    { value: 'original_document_seen', label: 'Original Document Seen' },
    { value: 'certified_copy_verified', label: 'Certified Copy Verified' },
    { value: 'digital_id_verification', label: 'Digital ID Verification' },
    { value: 'other', label: 'Other Documented Verification' }
  ],
  identity_verification: [
    { value: 'copy_verified', label: 'Uploaded copy reviewed (not original)' },
    { value: 'original_document_seen', label: 'Original Document Seen' },
    { value: 'certified_copy_verified', label: 'Certified Copy Verified' },
    { value: 'digital_id_verification', label: 'Digital ID Verification' },
    { value: 'other', label: 'Other Documented Verification' }
  ],
  
  // Proof of Address verification methods
  proof_of_address: [
    { value: 'original_document_seen', label: 'Original Document Seen' },
    { value: 'uploaded_copy_verified', label: 'Uploaded Copy Verified' },
    { value: 'certified_copy_verified', label: 'Certified Copy Verified' },
    { value: 'other', label: 'Other Documented Verification' }
  ],
  address_verification: [
    { value: 'original_seen', label: 'Original document seen in person' },
    { value: 'uploaded_copy', label: 'Uploaded copy reviewed' },
    { value: 'certified_copy', label: 'Certified copy reviewed' },
    { value: 'other', label: 'Other documented verification' }
  ],
  
  // Reference verification methods
  reference_1: [
    { value: 'email_verified', label: 'Reference verified by email' },
    { value: 'phone_verified', label: 'Reference verified by phone' },
    { value: 'written_reference', label: 'Written reference reviewed' },
    { value: 'employer_portal', label: 'Employer verification portal' },
    { value: 'other', label: 'Other documented verification' }
  ],
  reference_2: [
    { value: 'email_verified', label: 'Reference verified by email' },
    { value: 'phone_verified', label: 'Reference verified by phone' },
    { value: 'written_reference', label: 'Written reference reviewed' },
    { value: 'employer_portal', label: 'Employer verification portal' },
    { value: 'other', label: 'Other documented verification' }
  ],
  
  // Training / Qualifications verification methods
  training: [
    { value: 'certificate_reviewed', label: 'Certificate reviewed' },
    { value: 'provider_portal', label: 'Provider portal checked' },
    { value: 'uploaded_copy', label: 'Uploaded copy reviewed' },
    { value: 'register_checked', label: 'Third-party register checked' },
    { value: 'other', label: 'Other documented verification' }
  ],
  
  // NMC Registration verification (for nurses)
  nmc_registration: [
    { value: 'register_checked', label: 'NMC register checked online' },
    { value: 'pin_verified', label: 'NMC PIN verified' },
    { value: 'certificate_reviewed', label: 'Registration certificate reviewed' },
    { value: 'other', label: 'Other documented verification' }
  ],
  
  // Default fallback for any unrecognized check types
  default: [
    { value: 'original_seen', label: 'Original document seen in person' },
    { value: 'certified_copy', label: 'Certified copy reviewed' },
    { value: 'uploaded_copy', label: 'Uploaded copy reviewed' },
    { value: 'register_checked', label: 'Third-party register checked' },
    { value: 'other', label: 'Other documented verification' }
  ]
};

const CHECK_OUTCOMES = [
  { value: 'awaiting_review', label: 'Keep Pending — further employer review', color: 'text-amber-600' },
  { value: 'verified', label: 'Verified', color: 'text-green-600' },
  { value: 'failed', label: 'Rejected / action required', color: 'text-red-600' },
  { value: 'follow_up_required', label: 'Follow-up Required', color: 'text-amber-600' }
];

const OFFICIAL_CHECK_LINKS = {
  right_to_work: [
    {
      label: 'Open GOV.UK RTW share-code check',
      url: 'https://www.gov.uk/view-right-to-work',
      description: 'Use when the applicant gives you a Home Office share code.'
    },
    {
      label: 'Open Employer Checking Service',
      url: 'https://www.gov.uk/employee-immigration-employment-status',
      description: 'Use when the applicant cannot prove status through the normal route.'
    }
  ],
  dbs: DBS_CHECK_ROUTE_LINKS
};

const SOURCE_STATUS_TYPES = [
  { value: 'share_code', label: 'Share Code Check Result' },
  { value: 'digital_status', label: 'Digital Status (eVisa)' },
  { value: 'settled_status', label: 'Settled Status (EU Settlement Scheme)' },
  { value: 'pre_settled_status', label: 'Pre-Settled Status (EU Settlement Scheme)' },
  { value: 'uk_citizen', label: 'UK Citizen (Passport/Birth Certificate)' },
  { value: 'irish_citizen', label: 'Irish Citizen' },
  { value: 'brp_valid', label: 'BRP - Valid (with online check)' },
  { value: 'brp_expired', label: 'BRP - Expired (online check required)' },
  { value: 'passport_endorsement', label: 'Passport Endorsement' },
  { value: 'work_visa', label: 'Work Visa' },
  { value: 'student_visa', label: 'Student Visa (with work permission)' },
  { value: 'other', label: 'Other' }
];

// RTW-SPECIFIC GUIDANCE based on verification method
const RTW_METHOD_GUIDANCE = {
  home_office_online_check: {
    title: 'Home Office Online Check (Share Code)',
    guidance: 'You MUST verify this online via GOV.UK using the applicant\'s share code.',
    steps: [
      'Ask applicant for their 9-character share code',
      'Visit gov.uk/view-right-to-work',
      'Enter share code and applicant\'s date of birth',
      'Save/screenshot the result as proof'
    ],
    proofRequired: true,
    proofLabel: 'Home Office check result (screenshot/PDF)',
    link: 'https://www.gov.uk/view-right-to-work',
    badgeColor: 'bg-blue-100 text-blue-800 border-blue-200',
    route: 'home_office_online_check'
  },
  manual_passport_uk_irish: {
    title: 'Manual Check - UK/Irish Passport',
    guidance: 'Valid for UK/Irish citizens only. This is a List A document - unlimited right to work.',
    steps: [
      'Check passport is genuine (security features)',
      'Verify photo matches the applicant',
      'Check passport is current or expired (both valid for UK/Irish)',
      'Record passport number and any expiry date',
      'Take a clear copy for records',
      'Apply "Original Document Seen" verification stamp'
    ],
    proofRequired: false,
    stampRequired: true,
    stampType: 'original_seen',
    badgeColor: 'bg-green-100 text-green-800 border-green-200',
    route: 'manual_list_a_check',
    unlimited: true
  },
  manual_list_a_document: {
    title: 'Manual Check - List A Document',
    guidance: 'List A documents prove unlimited right to work in the UK.',
    steps: [
      'Verify document is genuine with security features',
      'Check document relates to the applicant',
      'Acceptable documents: UK/Irish passport (current/expired), Birth certificate + NI proof, Certificate of Registration/Naturalisation + NI proof, Indefinite Leave documents',
      'Take a clear copy for records',
      'Apply appropriate verification stamp'
    ],
    proofRequired: false,
    stampRequired: true,
    stampType: 'original_seen',
    badgeColor: 'bg-green-100 text-green-800 border-green-200',
    route: 'manual_list_a_check',
    unlimited: true
  },
  manual_list_b_group_1: {
    title: 'Manual Check - List B Group 1 (Time-Limited)',
    guidance: 'List B Group 1 documents prove time-limited right to work. FOLLOW-UP REQUIRED before expiry.',
    warning: 'You MUST set a follow-up date before the permission expires. The employee cannot work beyond this date without re-verification.',
    steps: [
      'Check document is genuine with security features',
      'Verify visa/permission is current (not expired)',
      'Record the permission END DATE carefully',
      'Check for any work restrictions',
      'Set follow-up date 28 days BEFORE permission expires',
      'Take a clear copy for records'
    ],
    proofRequired: false,
    stampRequired: true,
    stampType: 'original_seen',
    badgeColor: 'bg-amber-100 text-amber-800 border-amber-200',
    route: 'manual_list_b_group_1_check',
    unlimited: false,
    requiresFollowUp: true
  },
  manual_list_b_group_2_ecs: {
    title: 'Manual Check - List B Group 2 / ECS',
    guidance: 'For applicants with pending immigration applications. Requires Employer Checking Service verification.',
    warning: 'You MUST obtain a Positive Verification Notice (PVN) from the Home Office before employing.',
    steps: [
      'Check Certificate of Application or ARC card',
      'Submit request to Employer Checking Service (ECS)',
      'Wait for Positive Verification Notice (PVN)',
      'Record the PVN reference number',
      'Set 6-month follow-up for repeat ECS check',
      'Do NOT employ without valid PVN'
    ],
    proofRequired: true,
    proofLabel: 'Positive Verification Notice (PVN)',
    link: 'https://www.gov.uk/employee-immigration-employment-status',
    badgeColor: 'bg-purple-100 text-purple-800 border-purple-200',
    route: 'manual_list_b_group_2_check',
    unlimited: false,
    requiresFollowUp: true,
    requiresECS: true
  },
  idsp_check: {
    title: 'Digital Verification Service (IDSP)',
    guidance: 'Use an accredited Identity Service Provider for digital verification of British/Irish passports.',
    steps: [
      'Use an accredited IDSP from the government list',
      'IDSP can only verify British or Irish passports/passport cards',
      'Follow their digital verification process',
      'Retain the IDSP verification certificate',
      'Certificate must confirm document validity'
    ],
    proofRequired: true,
    proofLabel: 'IDSP Verification Certificate',
    badgeColor: 'bg-indigo-100 text-indigo-800 border-indigo-200',
    route: 'digital_verification_service_check',
    unlimited: true
  },
  ecs_pvn_check: {
    title: 'Employer Checking Service (PVN)',
    guidance: 'Use when applicant cannot provide acceptable documents or has pending immigration application.',
    warning: 'You MUST NOT employ someone based solely on their word. A valid PVN is required.',
    steps: [
      'Submit request via gov.uk/employee-immigration-employment-status',
      'Wait for Home Office response (usually within 5 working days)',
      'Receive Positive Verification Notice (PVN)',
      'Record the PVN reference number',
      'Set 6-month follow-up for repeat check',
      'Keep the PVN on file'
    ],
    proofRequired: true,
    proofLabel: 'Positive Verification Notice (PVN)',
    link: 'https://www.gov.uk/employee-immigration-employment-status',
    badgeColor: 'bg-purple-100 text-purple-800 border-purple-200',
    route: 'ecs_pvn_check',
    unlimited: false,
    requiresFollowUp: true
  }
};

// Map check types to requirement IDs for proof file storage
const CHECK_TYPE_TO_REQUIREMENT = {
  // Must align with backend check/evidence requirement IDs.
  right_to_work: 'right_to_work',
  right_to_work_check: 'right_to_work',
  dbs: 'dbs_check',
  dbs_status_check: 'dbs_check',
  identity: 'identity_documents',
  identity_verification: 'identity_documents',
  proof_of_address: 'proof_of_address',
  address_verification: 'proof_of_address'
};

/**
 * RecordCheckDialog - Dialog for recording employer verification checks
 * 
 * COMPLIANCE-CRITICAL: Requires proof file upload before check can be saved.
 * 
 * Supports:
 * - Right to Work Check
 * - DBS Status Check
 * - Identity Verification
 * - Address Verification
 */
export default function RecordCheckDialog({
  open,
  onClose,
  employeeId,
  checkType,
  onComplete,
  // Evidence status props for validation
  hasAcceptedEvidence = false,
  hasStampedEvidence = false,
  acceptedEvidenceCount = 0,
  existingCheck = null,
  existingProofAvailable = false,
  acceptedSourceFiles = [],
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [formData, setFormData] = useState({
    method: '',
    checked_at: new Date().toISOString().split('T')[0],
    outcome: ['dbs', 'dbs_status_check'].includes(checkType)
      ? 'follow_up_required'
      : ['identity', 'identity_verification', 'proof_of_address', 'address_verification'].includes(checkType)
        ? 'awaiting_review' : 'verified',
    source_status_type: '',
    follow_up_due_at: '',
    review_due_at: '',
    certificate_number: '',
    notes: '',
    // RTW Result Panel fields
    permission_type: '',  // e.g., British Citizen, Skilled Worker, Pre-Settled Status
    permission_start_date: '',
    permission_end_date: '',
    reference_number: '',
    share_code: '',
    restrictions: '',
    hours_limit: '',
    is_indefinite: false,
    follow_up_required: false,
    document_type: '',
    // DBS Result Panel fields
    dbs_level: '',
    certificate_issue_date: '',
    name_on_certificate: '',
    workforce: '',
    update_service_registered: false,
    update_service_status: '',
    last_status_check_date: '',
    update_service_check_result: '',
    recheck_required: true,
    next_recheck_date: '',
    result_status: '',
    information_present: false,
    result_summary: '',
    // Identity Result Panel fields
    id_document_type: '',
    id_full_name_on_document: '',
    id_date_of_birth: '',
    id_document_number: '',
    id_issue_date: '',
    id_expiry_date: '',
    id_nationality: '',
    id_name_matches_application: false,
    id_dob_matches_application: false,
    id_photo_match_confirmed: false,
    // Address Result Panel fields
    address_documents_received_count: 0,
    address_documents_required_count: 2,
    address_verified_documents: [],
    address_extracted_line1: '',
    address_extracted_line2: '',
    address_extracted_city: '',
    address_extracted_postcode: '',
    address_matches_application: false
  });
  
  // Extraction state for RTW and DBS
  const [extractionResult, setExtractionResult] = useState(null);
  const [extractionIssues, setExtractionIssues] = useState([]);
  
  // Proof file state - COMPLIANCE CRITICAL
  const [proofFile, setProofFile] = useState(null);
  const [uploadedProofId, setUploadedProofId] = useState(null);
  const [uploadedProofName, setUploadedProofName] = useState(null);
  const fileInputRef = useRef(null);
  
  // Progressive disclosure - collapse result details by default
  const [showResultDetails, setShowResultDetails] = useState(false);
  
  const { token } = useAuth();

  // Check if RTW check
  const isRTW = checkType === 'right_to_work_check' || checkType === 'right_to_work';
  
  // Check if DBS check
  const isDBS = checkType === 'dbs_status_check' || checkType === 'dbs';
  
  // Check if Identity check
  const isIdentity = checkType === 'identity_verification' || checkType === 'identity';
  
  // Check if Address check
  const isAddress = checkType === 'address_verification' || checkType === 'proof_of_address';
  const proofUploadRequired = isRTW || isDBS;

  // Get methods for this check type with fallback to default
  const methods = CHECK_METHODS[checkType] || CHECK_METHODS.default;

  // A correction must load the saved facts rather than start a blank new check.
  // Evidence is reused only if the tenant-scoped compliance workflow confirms availability.
  useEffect(() => {
    if (!open || !isRTW) return;
    const existing = getRTWCheckEditPrefill(existingCheck, existingProofAvailable);
    if (!existing) return;
    setFormData((current) => ({ ...current, ...existing.form }));
    setProofFile(null);
    setUploadedProofId(existing.proofId);
    setUploadedProofName(existing.proofName);
    setShowResultDetails(true);
    setExtractionResult(null);
    setExtractionIssues([]);
  }, [open, isRTW, existingCheck?.id, existingProofAvailable, employeeId]);

  // Reopen the saved, tenant-scoped DBS employer result and its current proof.
  // Do not populate any missing workforce or suitability decision by inference.
  useEffect(() => {
    if (!open || !isDBS) return;
    const existing = getDBSCheckEditPrefill(existingCheck, existingProofAvailable);
    if (!existing) return;
    setFormData(current => ({
      ...current,
      ...existing.form,
      method: methods.some(item => item.value === existing.form.method)
        ? existing.form.method : '',
    }));
    setProofFile(null);
    setUploadedProofId(existing.proofId);
    setUploadedProofName(existing.proofName);
    setShowResultDetails(true);
    setExtractionResult(null);
    setExtractionIssues([]);
  }, [open, isDBS, existingCheck?.id, existingProofAvailable, employeeId]);

  // Stamping ID evidence creates a pending employer check, not automatic clearance.
  // Restore the saved evidence link and facts. Pending matching confirmations
  // must be re-confirmed by an authorised manager before final verification.
  useEffect(() => {
    if (!open || (!isIdentity && !isAddress) || !existingCheck?.id) return;
    const check = existingCheck;
    const sourceId = check.proof_document_id || check.evidence_document_id || null;
    const securedSourceId = existingProofAvailable ? sourceId : null;
    const dateOnly = (value) => typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)
      ? value.slice(0, 10) : '';
    setFormData(current => ({
      ...current,
      method: methods.some(item => item.value === check.method) ? check.method : '',
      checked_at: dateOnly(check.checked_at) || current.checked_at,
      outcome: check.outcome === 'verified' ? 'verified' : 'awaiting_review',
      notes: check.notes || '',
      ...(isIdentity ? {
        id_document_type: check.document_type || '',
        id_full_name_on_document: check.full_name_on_document || '',
        id_date_of_birth: dateOnly(check.date_of_birth),
        id_document_number: check.document_number || '',
        id_issue_date: dateOnly(check.issue_date),
        id_expiry_date: dateOnly(check.expiry_date),
        id_nationality: check.nationality || '',
        id_name_matches_application: check.outcome === 'verified' && check.name_matches_application === true,
        id_dob_matches_application: check.outcome === 'verified' && check.dob_matches_application === true,
        id_photo_match_confirmed: check.outcome === 'verified' && check.photo_match_confirmed === true,
      } : {
        address_documents_received_count: check.documents_received_count || 0,
        address_documents_required_count: check.documents_required_count || 2,
        address_verified_documents: Array.isArray(check.verified_documents) ? check.verified_documents : [],
        address_extracted_line1: check.extracted_address_line1 || '',
        address_extracted_line2: check.extracted_address_line2 || '',
        address_extracted_city: check.extracted_city || '',
        address_extracted_postcode: check.extracted_postcode || '',
        address_matches_application: check.outcome === 'verified' && check.address_matches_application === true,
      }),
    }));
    setProofFile(null);
    setUploadedProofId(securedSourceId);
    setUploadedProofName(securedSourceId ? 'Existing accepted document — reused' : null);
    setShowResultDetails(true);
    setExtractionResult(null);
    setExtractionIssues([]);
  }, [open, isIdentity, isAddress, existingCheck?.id, existingProofAvailable, employeeId]);

  // Get title based on check type
  const getTitle = () => {
    switch (checkType) {
      case 'right_to_work':
      case 'right_to_work_check': return 'Record Right to Work Check';
      case 'dbs':
      case 'dbs_status_check': return 'Record DBS Status Check';
      case 'identity':
      case 'identity_verification': return 'Record Identity Verification';
      case 'proof_of_address':
      case 'address_verification': return 'Record Address Verification';
      default: return 'Record Check';
    }
  };

  // Get endpoint based on check type
  const getEndpoint = () => {
    switch (checkType) {
      case 'right_to_work':
      case 'right_to_work_check': 
        return `${API}/employees/${employeeId}/right-to-work/check`;
      case 'dbs':
      case 'dbs_status_check': 
        return `${API}/employees/${employeeId}/dbs/check`;
      case 'identity':
      case 'identity_verification': 
        return `${API}/employees/${employeeId}/identity/check`;
      case 'proof_of_address':
      case 'address_verification': 
        return `${API}/employees/${employeeId}/address/check`;
      default: 
        console.warn(`Unknown check type: ${checkType}`);
        return null;
    }
  };

  // Handle proof file selection - AUTO-EXTRACT for RTW
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload PDF, JPG, or PNG.');
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB.');
      return;
    }

    setProofFile(file);
    
    // AUTO-EXTRACT: Automatically extract fields when proof file is selected
    if (isRTW) {
      toast.info('Extracting RTW fields from document...', { duration: 2000 });
      await extractRTWFieldsFromFile(file);
    } else if (isDBS) {
      toast.info('Extracting DBS fields from document...', { duration: 2000 });
      await extractDBSFieldsFromFile(file);
    } else if (isIdentity) {
      toast.info('Extracting identity fields from document...', { duration: 2000 });
      await extractIdentityFieldsFromFile(file);
    } else if (isAddress) {
      toast.info('Extracting address fields from document...', { duration: 2000 });
      await extractAddressFieldsFromFile(file);
    }
  };

  // Extract DBS fields from a file using AI Vision
  const extractDBSFieldsFromFile = async (file) => {
    setIsExtracting(true);
    setExtractionResult(null);
    setExtractionIssues([]);

    try {
      // Convert file to base64
      const base64Data = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result.split(',')[1]);
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsDataURL(file);
      });

      const response = await axios.post(
        `${API}/dbs/extract`,
        {
          file_base64: base64Data,
          file_type: file.type
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { employee_id: employeeId }
        }
      );

      if (response.data?.success && response.data?.extraction) {
        const { fields, issues } = response.data.extraction;
        setExtractionResult(fields);
        setExtractionIssues(issues || []);
        
        // Log extraction results for debugging
        console.log('DBS Extraction result:', { fields, issues });

        // Auto-populate form fields from extraction
        setFormData(prev => ({
          ...prev,
          certificate_number: fields.certificate_number || prev.certificate_number,
          dbs_level: fields.dbs_level || prev.dbs_level,
          certificate_issue_date: fields.issue_date || prev.certificate_issue_date,
          name_on_certificate: fields.name_on_certificate || prev.name_on_certificate,
          workforce: fields.workforce || prev.workforce,
          result_status: fields.result_status || prev.result_status,
          information_present: fields.result_status === 'information_present' || prev.information_present,
          result_summary: fields.result_summary || fields.information_summary || prev.result_summary,
          update_service_status: fields.update_service_status || prev.update_service_status,
          update_service_registered: fields.update_service_status === 'active' || prev.update_service_registered,
          last_status_check_date: fields.last_status_check_date || prev.last_status_check_date,
          update_service_check_result: fields.update_service_check_result || prev.update_service_check_result
        }));

        // Check for blockers
        const blockers = (issues || []).filter(i => i.severity === 'blocker');
        const hasExtractedData = Object.keys(fields).some(k => fields[k] !== null && fields[k] !== undefined);
        
        if (blockers.length > 0) {
          toast.error(`Issue found: ${blockers[0].detail}`);
        } else if (hasExtractedData) {
          toast.success('DBS fields extracted - please review and confirm before saving');
        } else {
          toast.warning('Could not extract DBS data – please fill fields manually', { duration: 5000 });
          setExtractionIssues([...issues, {
            code: 'no_data_extracted',
            detail: 'No DBS fields could be extracted from this document. Please fill in the form manually.',
            severity: 'warning'
          }]);
        }
      } else {
        console.warn('DBS Extraction failed:', response.data);
        toast.warning('Could not extract DBS data – please fill fields manually', { duration: 5000 });
        setExtractionIssues([{
          code: 'extraction_failed',
          detail: response.data?.error || 'Extraction service unavailable. Please fill in the form manually.',
          severity: 'warning'
        }]);
      }
    } catch (err) {
      console.error('DBS Extraction error:', err);
      toast.info('Auto-extraction unavailable. Please fill in fields manually.');
    } finally {
      setIsExtracting(false);
    }
  };

  // Extract Identity fields from a file using AI Vision
  const extractIdentityFieldsFromFile = async (file) => {
    setIsExtracting(true);
    setExtractionResult(null);
    setExtractionIssues([]);

    try {
      const base64Data = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result.split(',')[1]);
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsDataURL(file);
      });

      const response = await axios.post(
        `${API}/identity/extract`,
        {
          file_base64: base64Data,
          file_type: file.type
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { employee_id: employeeId }
        }
      );

      if (response.data?.success && response.data?.extraction) {
        const { fields, issues } = response.data.extraction;
        setExtractionResult(fields);
        setExtractionIssues(issues || []);
        
        console.log('Identity Extraction result:', { fields, issues });

        setFormData(prev => ({
          ...prev,
          id_document_type: fields.document_type || prev.id_document_type,
          id_full_name_on_document: fields.full_name_on_document || prev.id_full_name_on_document,
          id_date_of_birth: fields.date_of_birth || prev.id_date_of_birth,
          id_document_number: fields.document_number || prev.id_document_number,
          id_issue_date: fields.issue_date || prev.id_issue_date,
          id_expiry_date: fields.expiry_date || prev.id_expiry_date,
          id_nationality: fields.nationality || prev.id_nationality,
          id_name_matches_application: fields.name_matches_application ?? prev.id_name_matches_application,
          id_dob_matches_application: fields.dob_matches_application ?? prev.id_dob_matches_application
        }));

        const blockers = (issues || []).filter(i => i.severity === 'blocker');
        const hasExtractedData = Object.keys(fields).some(k => fields[k] !== null && fields[k] !== undefined);
        
        if (blockers.length > 0) {
          toast.error(`Issue found: ${blockers[0].detail}`);
        } else if (hasExtractedData) {
          toast.success('Identity fields extracted - please review and confirm before saving');
        } else {
          toast.warning('Could not extract identity data – please fill fields manually', { duration: 5000 });
        }
      } else {
        toast.warning('Could not extract identity data – please fill fields manually', { duration: 5000 });
      }
    } catch (err) {
      console.error('Identity Extraction error:', err);
      toast.info('Auto-extraction unavailable. Please fill in fields manually.');
    } finally {
      setIsExtracting(false);
    }
  };

  // Extract Address fields from a file using AI Vision
  const extractAddressFieldsFromFile = async (file) => {
    setIsExtracting(true);
    setExtractionResult(null);
    setExtractionIssues([]);

    try {
      const base64Data = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result.split(',')[1]);
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsDataURL(file);
      });

      const response = await axios.post(
        `${API}/address/extract`,
        {
          file_base64: base64Data,
          file_type: file.type
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { employee_id: employeeId }
        }
      );

      if (response.data?.success && response.data?.extraction) {
        const { fields, issues } = response.data.extraction;
        setExtractionResult(fields);
        setExtractionIssues(issues || []);
        
        console.log('Address Extraction result:', { fields, issues });

        setFormData(prev => ({
          ...prev,
          address_extracted_line1: fields.address_line1 || prev.address_extracted_line1,
          address_extracted_line2: fields.address_line2 || prev.address_extracted_line2,
          address_extracted_city: fields.city || prev.address_extracted_city,
          address_extracted_postcode: fields.postcode || prev.address_extracted_postcode,
          address_matches_application: fields.address_matches_application ?? prev.address_matches_application
        }));

        const blockers = (issues || []).filter(i => i.severity === 'blocker');
        const hasExtractedData = Object.keys(fields).some(k => fields[k] !== null && fields[k] !== undefined);
        
        if (blockers.length > 0) {
          toast.error(`Issue found: ${blockers[0].detail}`);
        } else if (hasExtractedData) {
          toast.success('Address fields extracted - please review and confirm before saving');
          // Show recency status if available
          if (fields.recency_status?.status === 'outdated') {
            toast.warning(`Document is outdated (${fields.recency_status.days_old} days old)`, { duration: 5000 });
          } else if (fields.recency_status?.status === 'borderline') {
            toast.info(`Document is borderline (${fields.recency_status.days_old} days old)`);
          }
        } else {
          toast.warning('Could not extract address data – please fill fields manually', { duration: 5000 });
        }
      } else {
        toast.warning('Could not extract address data – please fill fields manually', { duration: 5000 });
      }
    } catch (err) {
      console.error('Address Extraction error:', err);
      toast.info('Auto-extraction unavailable. Please fill in fields manually.');
    } finally {
      setIsExtracting(false);
    }
  };

  // Extract RTW fields from a file using AI Vision
  const extractRTWFieldsFromFile = async (file) => {
    setIsExtracting(true);
    setExtractionResult(null);
    setExtractionIssues([]);

    try {
      // Convert file to base64
      const base64Data = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result.split(',')[1]);
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsDataURL(file);
      });

      const response = await axios.post(
        `${API}/rtw/extract`,
        {
          file_base64: base64Data,
          file_type: file.type
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { employee_id: employeeId }
        }
      );

      if (response.data?.success && response.data?.extraction) {
        const { fields, issues } = response.data.extraction;
        setExtractionResult(fields);
        setExtractionIssues(issues || []);
        
        // Log extraction results for debugging
        console.log('RTW Extraction result:', { fields, issues });

        // Auto-populate form fields from extraction
        setFormData(prev => ({
          ...prev,
          checked_at: fields.check_date || prev.checked_at,
          permission_start_date: fields.permission_start_date || prev.permission_start_date,
          permission_end_date: fields.permission_end_date || prev.permission_end_date,
          reference_number: fields.reference_number || prev.reference_number,
          share_code: fields.share_code || prev.share_code,
          restrictions: fields.restrictions || prev.restrictions,
          hours_limit: fields.hours_limit?.toString() || prev.hours_limit,
          is_indefinite: fields.is_indefinite ?? prev.is_indefinite,
          follow_up_required: fields.requires_followup ?? prev.follow_up_required,
          document_type: fields.document_type || prev.document_type,
          source_status_type: fields.permission_type ? mapPermissionTypeToStatus(fields.permission_type) : prev.source_status_type
        }));

        // Check for blockers
        const blockers = (issues || []).filter(i => i.severity === 'blocker');
        const hasExtractedData = Object.keys(fields).some(k => fields[k] !== null && fields[k] !== undefined);
        
        if (blockers.length > 0) {
          toast.error(`Issue found: ${blockers[0].detail}`);
        } else if (hasExtractedData) {
          toast.success('Fields extracted - please review and confirm before saving');
        } else {
          // No data extracted - show warning
          toast.warning('Could not extract RTW data – please fill fields manually', { duration: 5000 });
          setExtractionIssues([...issues, {
            code: 'no_data_extracted',
            detail: 'No RTW fields could be extracted from this document. Please fill in the form manually.',
            severity: 'warning'
          }]);
        }
      } else {
        // Extraction failed - show clear fallback message
        console.warn('RTW Extraction failed:', response.data);
        toast.warning('Could not extract RTW data – please fill fields manually', { duration: 5000 });
        setExtractionIssues([{
          code: 'extraction_failed',
          detail: response.data?.error || 'Extraction service unavailable. Please fill in the form manually.',
          severity: 'warning'
        }]);
      }
    } catch (err) {
      console.error('Extraction error:', err);
      toast.info('Auto-extraction unavailable. Please fill in fields manually.');
    } finally {
      setIsExtracting(false);
    }
  };

  // Upload proof file to employee_documents
  const uploadProofFile = async () => {
    if (!proofFile) return null;

    setIsUploading(true);
    try {
      const requirementId = CHECK_TYPE_TO_REQUIREMENT[checkType] || checkType;
      const headers = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data'
      };

      // Primary route (legacy/general upload path)
      let response;
      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', proofFile);
        formDataUpload.append('requirement_id', requirementId);
        formDataUpload.append('document_type', 'verification_proof');
        formDataUpload.append('document_label', `${getTitle()} - Proof`);
        response = await axios.post(
          `${API}/employees/${employeeId}/upload-document`,
          formDataUpload,
          { headers }
        );
      } catch (primaryErr) {
        // Fallback route (requirement-native evidence endpoint)
        const formDataEvidence = new FormData();
        formDataEvidence.append('file', proofFile);
        formDataEvidence.append('file_label', `${getTitle()} - Proof`);
        response = await axios.post(
          `${API}/employees/${employeeId}/requirements/${requirementId}/evidence`,
          formDataEvidence,
          { headers }
        );
      }

      const docId = response.data?.document_id || response.data?.id || response.data?.evidence_id;
      setUploadedProofId(docId);
      setUploadedProofName(proofFile.name);
      toast.success('Proof file uploaded successfully');
      return docId;
    } catch (err) {
      console.error('Failed to upload proof file:', err);
      const detail =
        err?.response?.data?.detail
        || err?.response?.data?.error
        || err?.response?.data?.message
        || 'Failed to upload proof file';
      toast.error(detail);
      return null;
    } finally {
      setIsUploading(false);
    }
  };

  // Remove uploaded proof
  const handleRemoveProof = () => {
    setProofFile(null);
    setUploadedProofId(null);
    setUploadedProofName(null);
    setExtractionResult(null);
    setExtractionIssues([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Re-extract fields (manual trigger for retry) - works for RTW, DBS, Identity, and Address
  const handleReExtract = async () => {
    if (!proofFile) return;
    if (isRTW) {
      await extractRTWFieldsFromFile(proofFile);
    } else if (isDBS) {
      await extractDBSFieldsFromFile(proofFile);
    } else if (isIdentity) {
      await extractIdentityFieldsFromFile(proofFile);
    } else if (isAddress) {
      await extractAddressFieldsFromFile(proofFile);
    }
  };

  // Map extracted permission type to source_status_type value
  const mapPermissionTypeToStatus = (permissionType) => {
    if (!permissionType) return '';
    const pt = permissionType.toLowerCase();
    if (pt.includes('settled') && !pt.includes('pre')) return 'settled_status';
    if (pt.includes('pre-settled') || pt.includes('presettled')) return 'pre_settled_status';
    if (pt.includes('uk citizen') || pt.includes('british')) return 'uk_citizen';
    if (pt.includes('irish')) return 'irish_citizen';
    if (pt.includes('evisa') || pt.includes('digital')) return 'digital_status';
    if (pt.includes('student')) return 'student_visa';
    if (pt.includes('work visa')) return 'work_visa';
    if (pt.includes('brp')) return 'brp_valid';
    return 'other';
  };

  const handleSubmit = async () => {
    const endpoint = getEndpoint();
    if (!endpoint) {
      toast.error('Invalid check type');
      return;
    }

    // Validation
    if (!formData.method) {
      toast.error('Please select a check method');
      return;
    }

    // RTW-SPECIFIC VALIDATION: Enforce proof upload for online check methods
    const isRTW = checkType === 'right_to_work_check' || checkType === 'right_to_work';
    const onlineCheckMethods = ['home_office_online_check', 'manual_list_b_group_2_ecs', 'ecs_pvn_check', 'idsp_check'];
    const methodGuidance = RTW_METHOD_GUIDANCE[formData.method];
    const requiresProof = isRTW && (onlineCheckMethods.includes(formData.method) || methodGuidance?.proofRequired);
    
    // COMPLIANCE-CRITICAL: Require proof file for online/ECS methods
    if (proofUploadRequired && !proofFile && !uploadedProofId) {
      if (requiresProof) {
        const proofLabel = methodGuidance?.proofLabel || 'verification proof';
        toast.error(`This method requires ${proofLabel}. This is a legal requirement.`);
        return;
      }
      // For manual checks, proof is still required but with different messaging
      toast.error('Upload proof of check before saving. This is required for compliance.');
      return;
    }
    
    // RTW-SPECIFIC: Block expired BRP without online check
    if (isRTW && formData.source_status_type === 'brp_expired') {
      toast.error('Expired BRP is not valid Right to Work evidence. Please use the Home Office Online Check (Share Code) method instead.');
      return;
    }

    // Prevent 'Verified' from being saved when the canonical backend
    // cannot accept the structured employer facts. This supplements (never
    // replaces) server-side readiness and evidence checks.
    const clearingRTW = isRTW && ['verified', 'follow_up_required'].includes(formData.outcome);
    if (clearingRTW && formData.is_indefinite) {
      if (!String(formData.permission_type || '').trim()) {
        toast.error('Record the indefinite immigration permission type shown by the Home Office or accepted evidence.');
        return;
      }
      if (formData.outcome !== 'verified') {
        toast.error('Confirm the correct check outcome. Indefinite permission does not require an expiry follow-up.');
        return;
      }
    }
    if (clearingRTW && !formData.is_indefinite) {
      if (!formData.permission_end_date) {
        toast.error('Enter the Right to Work permission end date.');
        return;
      }
      if (!formData.follow_up_required || !formData.follow_up_due_at) {
        toast.error('Time-limited Right to Work requires a follow-up date.');
        return;
      }
      if (!String(formData.restrictions || '').trim()) {
        toast.error("Review restrictions and enter them, or type 'None' if there are none.");
        return;
      }
    }

    const isDBS = checkType === 'dbs_status_check' || checkType === 'dbs';
    if (isDBS && formData.outcome === 'verified') {
      if (!formData.dbs_level || !formData.workforce || !String(formData.certificate_number || '').trim()) {
        toast.error('Enter the DBS level, workforce and certificate number.');
        return;
      }
      const result = formData.result_status || (formData.information_present ? 'information_present' : 'clear');
      if (!['clear', 'information_present'].includes(result)) {
        toast.error('Record the DBS result before verifying.');
        return;
      }
      if (result === 'information_present' && (
        !String(formData.result_summary || '').trim() ||
        !String(formData.notes || '').trim()
      )) {
        toast.error('Record the DBS disclosure summary and documented suitability decision.');
        return;
      }
      if (formData.method === 'dbs_update_service_check' &&
        (!formData.update_service_registered ||
         formData.update_service_status !== 'active' ||
         formData.update_service_check_result !== 'no_change' ||
         !formData.last_status_check_date)) {
        toast.error('Complete the DBS Update Service result and check date.');
        return;
      }
      if (formData.recheck_required !== false && !(formData.next_recheck_date || formData.review_due_at)) {
        toast.error('Set the DBS employer review date.');
        return;
      }
    }

    const isIdentity = checkType === 'identity_verification' || checkType === 'identity';
    if (isIdentity && formData.outcome === 'verified') {
      if (!uploadedProofId && !proofFile) {
        toast.error('A reviewed identity document must be linked to the formal check. Review the existing evidence before confirming.');
        return;
      }
      if (!formData.id_document_type || !String(formData.id_full_name_on_document || '').trim() || !formData.id_date_of_birth) {
        toast.error('Enter the identity document type, name and date of birth.');
        return;
      }
      if (!formData.id_name_matches_application || !formData.id_dob_matches_application) {
        toast.error('Confirm the identity name and date of birth match the application.');
        return;
      }
      if (!['birth_certificate', 'adoption_certificate'].includes(formData.id_document_type) &&
          !formData.id_photo_match_confirmed) {
        toast.error('Confirm the document photo matches the applicant.');
        return;
      }
    }

    if (isAddress && formData.outcome === 'verified') {
      if (!uploadedProofId && !proofFile) {
        toast.error('A reviewed address document must be linked to the formal check.');
        return;
      }
      if ((formData.address_verified_documents || []).length < 2 ||
          !formData.address_matches_application) {
        toast.error('Confirm two individually reviewed, valid address documents and their address match.');
        return;
      }
    }

    setIsSubmitting(true);
    let newlyUploadedProofDocId = null;
    try {
      // Upload proof first because the check record must retain its proof id.
      // If the check write fails, catch retires this new upload so retries do
      // not leave duplicate proof rows sitting in Evidence.
      let proofDocId = uploadedProofId;
      if (proofFile && !uploadedProofId) {
        proofDocId = await uploadProofFile();
        if (!proofDocId) {
          setIsSubmitting(false);
          return; // Upload failed, abort
        }
        newlyUploadedProofDocId = proofDocId;
      }

      // Build payload with evidence_document_id linking
      const payload = {
        method: formData.method,
        checked_at: formData.checked_at,
        outcome: formData.outcome,
        notes: formData.notes || null,
        evidence_document_id: proofDocId, // Legacy alias kept for backward compatibility
        proof_document_id: proofDocId // Canonical proof linkage for RTW/DBS
      };

      // Add type-specific fields
      if (checkType === 'right_to_work_check' || checkType === 'right_to_work') {
        // Core RTW fields
        payload.source_status_type = formData.source_status_type || null;
        payload.follow_up_due_at = formData.is_indefinite ? null : (formData.follow_up_due_at || null);
        
        // RTW Result Panel fields (3-layer model)
        payload.permission_type = formData.permission_type || null;  // e.g., British Citizen, Skilled Worker
        payload.permission_start_date = formData.permission_start_date || null;
        payload.permission_end_date = formData.is_indefinite ? null : (formData.permission_end_date || null);
        payload.reference_number = formData.reference_number || null;
        payload.share_code = formData.share_code || null;
        payload.restrictions = formData.restrictions || null;
        payload.hours_limit = formData.hours_limit ? parseInt(formData.hours_limit) : null;
        payload.is_indefinite = formData.is_indefinite || false;
        payload.follow_up_required = !formData.is_indefinite && formData.follow_up_required === true;
        payload.document_type = formData.document_type || null;
        
        // Route based on method
        const methodDef = methods.find(m => m.value === formData.method);
        payload.route = methodDef?.route || formData.method;
      }
      
      if (checkType === 'dbs_status_check' || checkType === 'dbs') {
        // DBS Result Panel fields (3-layer model)
        payload.dbs_level = formData.dbs_level || null;
        payload.certificate_number = formData.certificate_number || null;
        payload.certificate_issue_date = formData.certificate_issue_date || null;
        payload.name_on_certificate = formData.name_on_certificate || null;
        payload.workforce = formData.workforce || null;
        
        // Update Service specific
        payload.update_service_registered = formData.update_service_registered || false;
        payload.update_service_status = formData.update_service_status || null;
        payload.last_status_check_date = formData.last_status_check_date || null;
        payload.update_service_check_result = formData.update_service_check_result || null;
        
        // Recheck tracking
        payload.recheck_required = formData.recheck_required !== false; // Default true
        payload.next_recheck_date = formData.next_recheck_date || formData.review_due_at || null;
        payload.review_due_at = formData.review_due_at || formData.next_recheck_date || null;
        
        // Result details
        payload.result_status = formData.result_status || (formData.information_present ? 'information_present' : 'clear');
        payload.information_present = formData.information_present || false;
        payload.result_summary = formData.result_summary || null;
      }
      
      // Identity check payload
      if (checkType === 'identity_verification' || checkType === 'identity') {
        // Identity Result Panel fields (3-layer model)
        payload.document_type = formData.id_document_type || null;
        payload.full_name_on_document = formData.id_full_name_on_document || null;
        payload.date_of_birth = formData.id_date_of_birth || null;
        payload.document_number = formData.id_document_number || null;
        payload.issue_date = formData.id_issue_date || null;
        payload.expiry_date = formData.id_expiry_date || null;
        payload.nationality = formData.id_nationality || null;
        payload.name_matches_application = formData.id_name_matches_application || false;
        payload.dob_matches_application = formData.id_dob_matches_application || false;
        payload.photo_match_confirmed = formData.id_photo_match_confirmed || false;
        payload.proof_document_id = proofDocId;
      }
      
      // Address check payload
      if (checkType === 'address_verification' || checkType === 'proof_of_address') {
        // Address Result Panel fields (3-layer model)
        payload.documents_received_count = formData.address_documents_received_count || 0;
        payload.documents_required_count = formData.address_documents_required_count || 2;
        payload.verified_documents = formData.address_verified_documents || [];
        payload.extracted_address_line1 = formData.address_extracted_line1 || null;
        payload.extracted_address_line2 = formData.address_extracted_line2 || null;
        payload.extracted_city = formData.address_extracted_city || null;
        payload.extracted_postcode = formData.address_extracted_postcode || null;
        payload.address_matches_application = formData.address_matches_application || false;
        payload.proof_document_id = proofDocId;
      }

      await axios.post(endpoint, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(proofDocId ? 'Verification check recorded with proof' : 'Verification check recorded');
      if (onComplete) onComplete();
      handleClose();
    } catch (err) {
      // The proof upload is a preparatory side effect. If the authoritative
      // check record was not saved, retire only the proof created by this
      // submission attempt so the compliance file remains clean.
      if (newlyUploadedProofDocId) {
        try {
          await axios.post(
            `${API}/employee-documents/${newlyUploadedProofDocId}/mark-uploaded-in-error`,
            { reason: 'Record Check failed before the verification record could be saved' },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setUploadedProofId(null);
        } catch (cleanupErr) {
          console.error('Failed to retire proof upload after Record Check failure:', cleanupErr);
        }
      }
      toast.error(err.response?.data?.detail || 'Failed to record check');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      method: '',
      checked_at: new Date().toISOString().split('T')[0],
      outcome: ['dbs', 'dbs_status_check'].includes(checkType)
      ? 'follow_up_required'
      : ['identity', 'identity_verification', 'proof_of_address', 'address_verification'].includes(checkType)
        ? 'awaiting_review' : 'verified',
      source_status_type: '',
      follow_up_due_at: '',
      review_due_at: '',
      certificate_number: '',
      notes: '',
      // RTW Result Panel fields
      permission_type: '',
      permission_start_date: '',
      permission_end_date: '',
      reference_number: '',
      share_code: '',
      restrictions: '',
      hours_limit: '',
      is_indefinite: false,
      follow_up_required: false,
      document_type: '',
      // DBS Result Panel fields
      dbs_level: '',
      certificate_issue_date: '',
      name_on_certificate: '',
      workforce: '',
      update_service_registered: false,
      update_service_status: '',
      last_status_check_date: '',
      update_service_check_result: '',
      recheck_required: true,
      next_recheck_date: '',
      result_status: '',
      information_present: false,
      result_summary: '',
      // Identity Result Panel fields
      id_document_type: '',
      id_full_name_on_document: '',
      id_date_of_birth: '',
      id_document_number: '',
      id_issue_date: '',
      id_expiry_date: '',
      id_nationality: '',
      id_name_matches_application: false,
      id_dob_matches_application: false,
      id_photo_match_confirmed: false,
      // Address Result Panel fields
      address_documents_received_count: 0,
      address_documents_required_count: 2,
      address_verified_documents: [],
      address_extracted_line1: '',
      address_extracted_line2: '',
      address_extracted_city: '',
      address_extracted_postcode: '',
      address_matches_application: false
    });
    setProofFile(null);
    setUploadedProofId(null);
    setUploadedProofName(null);
    setExtractionResult(null);
    setExtractionIssues([]);
    if (onClose) onClose();
  };

  const hasProof = proofFile || uploadedProofId;
  const officialLinks = isRTW
    ? OFFICIAL_CHECK_LINKS.right_to_work
    : isDBS
      ? OFFICIAL_CHECK_LINKS.dbs
      : [];

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="flex h-[95vh] max-h-[95vh] w-[98vw] max-w-[98vw] flex-col overflow-hidden bg-white p-4 sm:max-w-[98vw]" data-testid="admin-verification-workbench-dialog">
        <DialogHeader className="shrink-0">
          <DialogTitle className="font-heading flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            {existingCheck?.id ? (isRTW ? 'Review / Correct Right to Work Check' : isDBS ? 'Finish / Correct DBS Check' : 'Finish / Correct Formal Check') : getTitle()}
          </DialogTitle>
          <DialogDescription>
            {proofUploadRequired
              ? 'Record the employer verification check with proof. Both the check record and proof file are required for compliance.'
              : 'Record the employer verification check. Accepted evidence remains the evidence layer; AI extraction is only supporting information.'}
          </DialogDescription>
          {isRTW && existingCheck?.id && (
            <p className="text-xs rounded-lg border border-blue-200 bg-blue-50 text-blue-900 px-3 py-2" role="note" data-testid="rtw-edit-existing-notice">
              This corrects an existing employer check. Confirm the saved Home Office result before saving.
              The prior check remains in the audit history and valid linked proof can be reused.
              Routine follow-up is not required only when immigration permission is genuinely indefinite.
            </p>
          )}
          {isDBS && existingCheck?.id && (
            <p className="text-xs rounded-lg border border-blue-200 bg-blue-50 text-blue-900 px-3 py-2" role="note" data-testid="dbs-edit-existing-notice">
              Your existing DBS check details are loaded below. Confirm the certificate's level,
              workforce, original outcome and suitability for this role before correcting it.
              Existing secured proof is reused where available. No new certificate upload is
              needed solely to complete a missing workforce field.
            </p>
          )}
          {(isIdentity || isAddress) && existingCheck?.id && (
            <p className="text-xs rounded-lg border border-blue-200 bg-blue-50 text-blue-900 px-3 py-2" role="note" data-testid="existing-identity-address-check-notice">
              The document review was recorded, but the formal employer check is separate.
              Confirm the original evidence, the person's details and required matches before choosing Verified.
              The earlier check remains part of the audit history; no new upload is required if its accepted source is available.
            </p>
          )}
        </DialogHeader>

        <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 overflow-y-auto lg:grid-cols-[minmax(0,1.6fr)_minmax(390px,1fr)] lg:overflow-hidden">
          <div className="min-h-[28rem] min-w-0 lg:min-h-0" data-testid="verification-evidence-side-by-side">
            <VerificationEvidenceWorkbench
              open={open} employeeId={employeeId} checkType={checkType} token={token}
            />
          </div>
          <div className="min-h-[36rem] min-w-0 overflow-y-auto rounded-xl border bg-white p-3 lg:min-h-0" data-testid="verification-form-alongside-evidence">
        <div className="space-y-4 py-4">
          {/* COMPLIANCE CHECKLIST - Consolidated warning */}
          {(!hasAcceptedEvidence || (hasAcceptedEvidence && !hasStampedEvidence)) && (
            <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
              <p className="text-xs font-medium text-slate-700 mb-2">Pre-check Checklist</p>
              <div className="space-y-1.5">
                <div className={`flex items-center gap-2 text-xs ${hasAcceptedEvidence ? 'text-green-600' : 'text-amber-600'}`}>
                  {hasAcceptedEvidence ? <CheckCircle className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}
                  <span>{hasAcceptedEvidence ? 'Evidence accepted' : 'Accept at least one evidence file first'}</span>
                </div>
                {hasAcceptedEvidence && (
                  <div className={`flex items-center gap-2 text-xs ${hasStampedEvidence ? 'text-green-600' : 'text-slate-500'}`}>
                    {hasStampedEvidence ? <CheckCircle className="h-3 w-3" /> : <Info className="h-3 w-3" />}
                    <span>{hasStampedEvidence ? 'Evidence stamped' : 'Optional: Apply verification stamp to evidence'}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {officialLinks.length > 0 && (
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
              <div className="flex items-start gap-2">
                <Info className="h-4 w-4 mt-0.5 flex-shrink-0 text-blue-700" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-blue-900">
                    {isDBS ? 'DBS — choose the correct route' : 'Official check route'}
                  </p>
                  <p className="text-xs text-blue-800">
                    {isDBS
                      ? 'An existing subscribed DBS certificate needs an employer status check. A new Care Check application is a separate process arranged by Osabea. Only record a completed, evidenced DBS check here.'
                      : 'Open the official service first, then return here to record the result and upload the required proof.'}
                  </p>
                  <div className="mt-3 grid gap-2">
                    {officialLinks.map((link) => (
                      <a
                        key={link.url}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="rounded-md border border-blue-200 bg-white px-3 py-2 text-xs text-blue-900 shadow-sm hover:bg-blue-100"
                      >
                        <span className="flex items-center gap-1 font-semibold">
                          <ExternalLink className="h-3 w-3" />
                          {link.label}
                        </span>
                        <span className="mt-0.5 block text-blue-700">
                          {link.description}
                        </span>
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Reuse accepted tenant-owned evidence; never reupload only to link it. */}
          {(isIdentity || isAddress) && acceptedSourceFiles.length > 0 && (
            <div className="space-y-2 rounded-lg border border-blue-200 bg-blue-50 p-3" data-testid="existing-accepted-source-selector">
              <Label>Use an existing accepted document</Label>
              <Select value={uploadedProofId || ''}
                onValueChange={id => {
                  setProofFile(null);
                  setUploadedProofId(id);
                  setUploadedProofName('Existing accepted source document');
                }}>
                <SelectTrigger data-testid="accepted-source-select">
                  <SelectValue placeholder="Choose the document already reviewed" />
                </SelectTrigger>
                <SelectContent>
                  {acceptedSourceFiles.map((source, index) => (
                    <SelectItem key={source.id} value={source.id}>
                      Accepted document {index + 1}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-blue-900">
                Only accepted, available evidence from this applicant is offered.
                The server checks employee, tenant and document type before saving.
              </p>
            </div>
          )}
          {/* PROOF FILE UPLOAD */}
          <div className="space-y-2">
            <Label className="flex items-center gap-1">
              {isIdentity ? 'Source identity evidence' : isAddress ? 'Source address evidence' : 'Proof of Check'}
              {' '}{proofUploadRequired ? '*' : isIdentity || isAddress ? '(link accepted evidence)' : '(optional)'}
              {hasProof && <CheckCircle className="h-4 w-4 text-green-600" />}
            </Label>
            
            {!hasProof ? (
              <div 
                className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary cursor-pointer transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                <p className="text-sm text-gray-600">Click to upload proof file</p>
                <p className="text-xs text-gray-400 mt-1">PDF, JPG, PNG (max 10MB)</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleFileSelect}
                />
              </div>
            ) : (
              <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-green-600" />
                  <div>
                    <p className="text-sm font-medium text-green-800">
                      {uploadedProofName || proofFile?.name}
                    </p>
                    <p className="text-xs text-green-600">
                      {uploadedProofId && !proofFile && existingCheck?.id && uploadedProofId === (existingCheck.proof_document_id || existingCheck.evidence_document_id)
                        ? 'Existing secured proof — will be reused'
                        : uploadedProofId ? 'Uploaded' : 'Ready to upload'}
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRemoveProof}
                  className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>

          {/* Extraction Status & Re-extract Option - RTW, DBS, Identity, and Address */}
          {(isRTW || isDBS || isIdentity || isAddress) && hasProof && (
            <div className="space-y-2">
              {/* Extraction in progress */}
              {isExtracting && (
                <div className="flex items-center gap-2 p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                  <Loader2 className="h-4 w-4 animate-spin text-indigo-600" />
                  <span className="text-sm text-indigo-700">
                    Extracting {isRTW ? 'RTW' : isDBS ? 'DBS' : isIdentity ? 'identity' : 'address'} fields from document...
                  </span>
                </div>
              )}
              
              {/* Extraction complete indicator */}
              {!isExtracting && extractionResult && Object.keys(extractionResult).some(k => extractionResult[k] !== null) && (
                <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm text-green-700">Fields extracted - please review below</span>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleReExtract}
                    disabled={isExtracting}
                    className="h-7 px-2 text-xs text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
                    data-testid={`${isRTW ? 'rtw' : 'dbs'}-re-extract-btn`}
                  >
                    <RefreshCw className="h-3 w-3 mr-1" />
                    Re-extract
                  </Button>
                </div>
              )}
              
              {/* Manual extract button - only if no extraction yet and not currently extracting */}
              {!isExtracting && !extractionResult && !uploadedProofId && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleReExtract}
                  disabled={isExtracting}
                  className="w-full h-9 text-sm bg-indigo-50 border-indigo-200 text-indigo-700 hover:bg-indigo-100"
                  data-testid={`${isRTW ? 'rtw' : 'dbs'}-auto-extract-btn`}
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Extract {isRTW ? 'RTW' : 'DBS'} Fields
                </Button>
              )}
            </div>
          )}

          {/* Extraction Issues Warning */}
          {extractionIssues.length > 0 && (
            <div className="space-y-2">
              {extractionIssues.filter(i => i.severity === 'blocker').map((issue, idx) => (
                <div key={idx} className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-red-800">
                      <p className="font-medium">Blocker: {issue.code.replace(/_/g, ' ')}</p>
                      <p className="text-xs mt-0.5">{issue.detail}</p>
                    </div>
                  </div>
                </div>
              ))}
              {extractionIssues.filter(i => i.severity === 'warning').map((issue, idx) => (
                <div key={idx} className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-amber-800">
                      <p className="font-medium">Warning: {issue.code.replace(/_/g, ' ')}</p>
                      <p className="text-xs mt-0.5">{issue.detail}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Method */}
          <div className="space-y-2">
            <Label>Check Method *</Label>
            <Select 
              value={formData.method} 
              onValueChange={(v) => setFormData(prev => ({ ...prev, method: v }))}
            >
              <SelectTrigger className="rounded-lg">
                <SelectValue placeholder="Select check method" />
              </SelectTrigger>
              <SelectContent>
                {methods.map(method => (
                  <SelectItem key={method.value} value={method.value}>
                    <span className="flex items-center gap-2">
                      {method.label}
                      {method.recommended && (
                        <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">Recommended</span>
                      )}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* RTW-SPECIFIC GUIDANCE BOX - Shows method-specific instructions */}
          {(checkType === 'right_to_work_check' || checkType === 'right_to_work') && formData.method && RTW_METHOD_GUIDANCE[formData.method] && (
            <div className={`p-4 rounded-lg border ${RTW_METHOD_GUIDANCE[formData.method].badgeColor || 'bg-blue-50 border-blue-200'}`}>
              <div className="space-y-3">
                <div className="flex items-start gap-2">
                  <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-sm">{RTW_METHOD_GUIDANCE[formData.method].title}</p>
                    <p className="text-xs mt-1">{RTW_METHOD_GUIDANCE[formData.method].guidance}</p>
                  </div>
                </div>
                
                {/* Warning (e.g., BRP expiry) */}
                {RTW_METHOD_GUIDANCE[formData.method].warning && (
                  <div className="p-2 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-red-700 font-medium">{RTW_METHOD_GUIDANCE[formData.method].warning}</p>
                    </div>
                  </div>
                )}
                
                {/* Steps */}
                {RTW_METHOD_GUIDANCE[formData.method].steps && (
                  <div className="space-y-1">
                    <p className="text-xs font-medium">Steps:</p>
                    <ol className="text-xs space-y-1 list-decimal list-inside">
                      {RTW_METHOD_GUIDANCE[formData.method].steps.map((step, i) => (
                        <li key={i}>{step}</li>
                      ))}
                    </ol>
                  </div>
                )}
                
                {/* GOV.UK Link */}
                {RTW_METHOD_GUIDANCE[formData.method].link && (
                  <a 
                    href={RTW_METHOD_GUIDANCE[formData.method].link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-blue-700 hover:text-blue-900 font-medium"
                  >
                    <ExternalLink className="h-3 w-3" />
                    Open GOV.UK verification page
                  </a>
                )}
                
                {/* Proof requirement indicator */}
                {RTW_METHOD_GUIDANCE[formData.method].proofRequired && (
                  <div className="flex items-center gap-2 pt-2 border-t border-current/10">
                    <Upload className="h-3 w-3" />
                    <span className="text-xs font-medium">
                      Required proof: {RTW_METHOD_GUIDANCE[formData.method].proofLabel}
                    </span>
                  </div>
                )}
                
                {/* Stamp requirement indicator */}
                {RTW_METHOD_GUIDANCE[formData.method].stampRequired && (
                  <div className="flex items-center gap-2 pt-2 border-t border-current/10">
                    <CheckCircle className="h-3 w-3" />
                    <span className="text-xs font-medium">
                      Apply verification stamp: "{RTW_METHOD_GUIDANCE[formData.method].stampType === 'original_seen' ? 'Original Document Seen' : 'Copy Verified'}"
                    </span>
                  </div>
                )}
                
                {/* Follow-up requirement indicator */}
                {RTW_METHOD_GUIDANCE[formData.method].requiresFollowUp && (
                  <div className="flex items-center gap-2 pt-2 border-t border-current/10">
                    <AlertTriangle className="h-3 w-3 text-amber-600" />
                    <span className="text-xs font-medium text-amber-700">
                      Time-limited permission - Follow-up date REQUIRED
                    </span>
                  </div>
                )}
                
                {/* Unlimited right indicator */}
                {RTW_METHOD_GUIDANCE[formData.method].unlimited && (
                  <div className="flex items-center gap-2 pt-2 border-t border-current/10">
                    <CheckCircle className="h-3 w-3 text-green-600" />
                    <span className="text-xs font-medium text-green-700">
                      Unlimited right to work - No follow-up required
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Checked At */}
          <div className="space-y-2">
            <Label>Date Checked *</Label>
            <Input
              type="date"
              value={formData.checked_at}
              onChange={(e) => setFormData(prev => ({ ...prev, checked_at: e.target.value }))}
              className="rounded-lg"
            />
          </div>

          {/* Outcome */}
          <div className="space-y-2">
            <Label>Outcome *</Label>
            <Select 
              value={formData.outcome} 
              onValueChange={(v) => setFormData(prev => ({ ...prev, outcome: v }))}
            >
              <SelectTrigger className="rounded-lg">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CHECK_OUTCOMES.map(outcome => (
                  <SelectItem key={outcome.value} value={outcome.value}>
                    <span className={outcome.color}>{outcome.label}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {['dbs', 'dbs_status_check'].includes(checkType) && (
              <p className="text-xs text-text-muted">
                {formData.outcome === 'verified'
                  ? 'Approve only after reviewing the original certificate, matching the person’s identity and checking that the level and workforce suit this role.'
                  : 'Save the review now. DBS remains under review and does not clear this person for work.'}
              </p>
            )}
          </div>

          {/* RTW-specific: Source Status Type */}
          {checkType === 'right_to_work_check' && (
            <div className="space-y-2">
              <Label>Source Status Type</Label>
              <Select 
                value={formData.source_status_type} 
                onValueChange={(v) => setFormData(prev => ({ ...prev, source_status_type: v }))}
              >
                <SelectTrigger className="rounded-lg">
                  <SelectValue placeholder="Select status type" />
                </SelectTrigger>
                <SelectContent>
                  {SOURCE_STATUS_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* RTW-specific: Follow-up Due */}
          {checkType === 'right_to_work_check' && (
            <div className="space-y-2">
              <Label>Follow-up Due Date</Label>
              <Input
                type="date"
                value={formData.follow_up_due_at}
                onChange={(e) => setFormData(prev => ({ ...prev, follow_up_due_at: e.target.value }))}
                className="rounded-lg"
              />
              <p className="text-xs text-text-muted">
                For time-limited permissions, set when the next check is due
              </p>
            </div>
          )}

          {/* ============================================== */}
          {/* RTW RESULT PANEL - Permission Details         */}
          {/* 3-Layer Model: Evidence -> Verification -> Result */}
          {/* ============================================== */}
          {isRTW && (
            <div className="space-y-4 p-4 bg-slate-50 border border-slate-200 rounded-xl">
              <button
                type="button"
                className="w-full flex items-center justify-between"
                onClick={() => setShowResultDetails(!showResultDetails)}
              >
                <div className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-slate-600" />
                  <h4 className="text-sm font-semibold text-slate-800">Right to Work Result</h4>
                  {extractionResult && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded">
                      AI Extracted
                    </span>
                  )}
                </div>
                <ChevronDown className={`h-4 w-4 text-slate-500 transition-transform ${showResultDetails ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Collapsed preview - show key info */}
              {!showResultDetails && (formData.permission_type || extractionResult) && (
                <div className="text-sm text-slate-600 bg-white p-2 rounded border border-slate-100">
                  {formData.permission_type && <span className="font-medium">{formData.permission_type}</span>}
                  {formData.permission_end_date && <span className="ml-2 text-slate-500">• Expires: {formData.permission_end_date}</span>}
                  {formData.share_code && <span className="ml-2 text-slate-500">• Code: {formData.share_code}</span>}
                </div>
              )}
              
              {/* Expandable details */}
              {showResultDetails && (
                <div className="space-y-4 pt-2 border-t border-slate-200">
              
              {/* Permission Type - Full width, prominent */}
              <div className="space-y-1">
                <Label className="text-xs font-semibold">Immigration Permission Type *</Label>
                <Input
                  value={formData.permission_type}
                  onChange={(e) => setFormData(prev => ({ ...prev, permission_type: e.target.value }))}
                  placeholder="e.g., British Citizen, Skilled Worker, Pre-Settled Status"
                  className="h-9 text-sm rounded-lg"
                  data-testid="rtw-permission-type"
                />
                <p className="text-xs text-text-muted">
                  The immigration status or visa type (e.g., British Citizen, Skilled Worker Visa, Pre-Settled Status)
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                {/* Permission Start Date */}
                <div className="space-y-1">
                  <Label className="text-xs">Permission Start</Label>
                  <Input
                    type="date"
                    value={formData.permission_start_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, permission_start_date: e.target.value }))}
                    className="h-8 text-sm rounded-lg"
                    data-testid="rtw-permission-start"
                  />
                </div>
                
                {/* Permission End Date */}
                <div className="space-y-1">
                  <Label className="text-xs">Permission End / Expiry</Label>
                  <Input
                    type="date"
                    value={formData.permission_end_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, permission_end_date: e.target.value }))}
                    className="h-8 text-sm rounded-lg"
                    data-testid="rtw-permission-end"
                  />
                </div>
                
                {/* Reference Number */}
                <div className="space-y-1">
                  <Label className="text-xs">Reference / PVN Number</Label>
                  <Input
                    value={formData.reference_number}
                    onChange={(e) => setFormData(prev => ({ ...prev, reference_number: e.target.value }))}
                    placeholder="e.g., PVN123456"
                    className="h-8 text-sm rounded-lg"
                    data-testid="rtw-reference-number"
                  />
                </div>
                
                {/* Share Code */}
                <div className="space-y-1">
                  <Label className="text-xs">Share Code</Label>
                  <Input
                    value={formData.share_code}
                    onChange={(e) => setFormData(prev => ({ ...prev, share_code: e.target.value.toUpperCase() }))}
                    placeholder="e.g., ABC123DEF"
                    maxLength={9}
                    className="h-8 text-sm rounded-lg font-mono"
                    data-testid="rtw-share-code"
                  />
                </div>
              </div>
              
              {/* Restrictions */}
              <div className="space-y-1">
                <Label className="text-xs">Work Restrictions</Label>
                <Input
                  value={formData.restrictions}
                  onChange={(e) => setFormData(prev => ({ ...prev, restrictions: e.target.value }))}
                  placeholder="e.g., 20 hours per week during term time"
                  className="h-8 text-sm rounded-lg"
                  data-testid="rtw-restrictions"
                />
              </div>
              
              {/* Hours Limit */}
              {formData.restrictions && (
                <div className="space-y-1">
                  <Label className="text-xs">Hours Limit (per week)</Label>
                  <Input
                    type="number"
                    value={formData.hours_limit}
                    onChange={(e) => setFormData(prev => ({ ...prev, hours_limit: e.target.value }))}
                    placeholder="e.g., 20"
                    min={0}
                    max={48}
                    className="h-8 text-sm rounded-lg w-24"
                    data-testid="rtw-hours-limit"
                  />
                </div>
              )}
              
              {/* Checkboxes for status flags */}
              <div className="flex items-center gap-6 pt-2">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_indefinite}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_indefinite: e.target.checked, ...(e.target.checked ? { follow_up_required: false, follow_up_due_at: '', permission_end_date: '' } : {}) }))}
                    className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                    data-testid="rtw-is-indefinite"
                  />
                  <span className="text-slate-700">Indefinite immigration permission (no expiry date)</span>
                </label>
                
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!formData.is_indefinite && formData.follow_up_required}
                    disabled={formData.is_indefinite}
                    onChange={(e) => setFormData(prev => ({ ...prev, follow_up_required: e.target.checked }))}
                    className="w-4 h-4 rounded border-gray-300 text-amber-500 focus:ring-amber-500"
                    data-testid="rtw-follow-up-required"
                  />
                  <span className="text-slate-700">Follow-up required</span>
                </label>
              </div>
              
              <p className="text-xs text-slate-600" data-testid="rtw-duration-guidance">
                Unlimited working hours on a temporary visa does NOT mean indefinite immigration permission.
                Select indefinite only if the Home Office result or acceptable List A evidence confirms no immigration expiry.
                Otherwise record the actual permission end date and follow-up.
              </p>
              {/* Auto-calculation hint */}
              {!formData.is_indefinite && formData.permission_end_date && !formData.follow_up_due_at && (
                <div className="p-2 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-xs text-amber-700">
                    <AlertTriangle className="h-3 w-3 inline mr-1" />
                    Permission ends {formData.permission_end_date}. Set a follow-up date 28 days before.
                  </p>
                </div>
              )}
                </div>
              )}
            </div>
          )}

          {/* DBS-specific: Certificate Number */}
          {isDBS && (
            <div className="space-y-2">
              <Label>Certificate Number *</Label>
              <Input
                value={formData.certificate_number}
                onChange={(e) => setFormData(prev => ({ ...prev, certificate_number: e.target.value.replace(/\s/g, '') }))}
                placeholder="12-digit certificate number"
                maxLength={12}
                className="rounded-lg font-mono"
                data-testid="dbs-certificate-number"
              />
              {formData.certificate_number && formData.certificate_number.length !== 12 && (
                <p className="text-xs text-amber-600">Certificate number should be 12 digits</p>
              )}
            </div>
          )}

          {/* DBS-specific: Review Due */}
          {isDBS && (
            <div className="space-y-2">
              <Label>Next Recheck Date</Label>
              <Input
                type="date"
                value={formData.review_due_at || formData.next_recheck_date}
                onChange={(e) => setFormData(prev => ({ ...prev, review_due_at: e.target.value, next_recheck_date: e.target.value }))}
                className="rounded-lg"
                data-testid="dbs-review-due"
              />
              <p className="text-xs text-text-muted">
                Internal policy date for next review (DBS certificates don't have a statutory expiry)
              </p>
            </div>
          )}

          {/* ============================================== */}
          {/* DBS RESULT PANEL - Certificate Details         */}
          {/* 3-Layer Model: Evidence -> Verification -> Result */}
          {/* ============================================== */}
          {isDBS && (
            <div className="space-y-4 p-4 bg-slate-50 border border-slate-200 rounded-xl">
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4 text-slate-600" />
                <h4 className="text-sm font-semibold text-slate-800">DBS Result</h4>
                {extractionResult && (
                  <span className="text-[10px] px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded">
                    AI Extracted
                  </span>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                {/* DBS Level */}
                <div className="space-y-1">
                  <Label className="text-xs">DBS Level *</Label>
                  <Select 
                    value={formData.dbs_level} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, dbs_level: v }))}
                  >
                    <SelectTrigger className="h-8 text-sm rounded-lg" data-testid="dbs-level">
                      <SelectValue placeholder="Select level" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="basic">Basic</SelectItem>
                      <SelectItem value="standard">Standard</SelectItem>
                      <SelectItem value="enhanced">Enhanced</SelectItem>
                      <SelectItem value="enhanced_barred">Enhanced with Barred Lists</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Certificate Issue Date */}
                <div className="space-y-1">
                  <Label className="text-xs">Issue Date</Label>
                  <Input
                    type="date"
                    value={formData.certificate_issue_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, certificate_issue_date: e.target.value }))}
                    className="h-8 text-sm rounded-lg"
                    data-testid="dbs-issue-date"
                  />
                </div>
                
                {/* Name on Certificate */}
                <div className="space-y-1">
                  <Label className="text-xs">Name on Certificate</Label>
                  <Input
                    value={formData.name_on_certificate}
                    onChange={(e) => setFormData(prev => ({ ...prev, name_on_certificate: e.target.value }))}
                    placeholder="As shown on certificate"
                    className="h-8 text-sm rounded-lg"
                    data-testid="dbs-name"
                  />
                </div>
                
                {/* Workforce Type */}
                <div className="space-y-1">
                  <Label className="text-xs">Workforce</Label>
                  <Select 
                    value={formData.workforce} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, workforce: v }))}
                  >
                    <SelectTrigger className="h-8 text-sm rounded-lg" data-testid="dbs-workforce">
                      <SelectValue placeholder="Select workforce" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="adult">Adult Workforce</SelectItem>
                      <SelectItem value="child">Child Workforce</SelectItem>
                      <SelectItem value="adult_and_child">Adult and Child Workforce</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Update Service Section - only show for dbs_update_service_check method */}
              {formData.method === 'dbs_update_service_check' && (
                <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg space-y-3">
                  <div className="flex items-center gap-2">
                    <Shield className="h-3 w-3 text-indigo-600" />
                    <span className="text-xs font-semibold text-indigo-800">Update Service Check</span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    {/* Update Service Status */}
                    <div className="space-y-1">
                      <Label className="text-xs">Update Service Status</Label>
                      <Select 
                        value={formData.update_service_status} 
                        onValueChange={(v) => setFormData(prev => ({ 
                          ...prev, 
                          update_service_status: v,
                          update_service_registered: v === 'active'
                        }))}
                      >
                        <SelectTrigger className="h-8 text-sm rounded-lg" data-testid="dbs-update-status">
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">Active (Registered)</SelectItem>
                          <SelectItem value="not_registered">Not Registered</SelectItem>
                          <SelectItem value="expired">Expired</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Last Status Check Date */}
                    <div className="space-y-1">
                      <Label className="text-xs">Check Date</Label>
                      <Input
                        type="date"
                        value={formData.last_status_check_date}
                        onChange={(e) => setFormData(prev => ({ ...prev, last_status_check_date: e.target.value }))}
                        className="h-8 text-sm rounded-lg"
                        data-testid="dbs-last-check-date"
                      />
                    </div>
                  </div>
                  
                  {/* Update Service Check Result */}
                  <div className="space-y-1">
                    <Label className="text-xs">Check Result</Label>
                    <Select 
                      value={formData.update_service_check_result} 
                      onValueChange={(v) => setFormData(prev => ({ ...prev, update_service_check_result: v }))}
                    >
                      <SelectTrigger className="h-8 text-sm rounded-lg" data-testid="dbs-check-result">
                        <SelectValue placeholder="Select result" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="no_change">No change to disclose</SelectItem>
                        <SelectItem value="changed">Changed - New certificate required</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Warning if changed */}
                  {formData.update_service_check_result === 'changed' && (
                    <div className="p-2 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-xs text-red-700 font-medium">
                        <AlertTriangle className="h-3 w-3 inline mr-1" />
                        Update Service shows changes. Request a new DBS certificate disclosure.
                      </p>
                    </div>
                  )}
                </div>
              )}
              
              {/* Result Status Section */}
              <div className="space-y-3 pt-3 border-t border-slate-200">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-slate-700">Result Status</span>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  {/* Result Status */}
                  <div className="space-y-1">
                    <Label className="text-xs">Clearance Status</Label>
                    <Select 
                      value={formData.result_status || (formData.information_present ? 'information_present' : 'clear')} 
                      onValueChange={(v) => setFormData(prev => ({ 
                        ...prev, 
                        result_status: v,
                        information_present: v === 'information_present'
                      }))}
                    >
                      <SelectTrigger className="h-8 text-sm rounded-lg" data-testid="dbs-result-status">
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="clear">Clear - No information</SelectItem>
                        <SelectItem value="information_present">Information Present</SelectItem>
                        <SelectItem value="pending_review">Pending Review</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Recheck Required checkbox */}
                  <div className="flex items-end">
                    <label className="flex items-center gap-2 text-sm cursor-pointer pb-2">
                      <input
                        type="checkbox"
                        checked={formData.recheck_required !== false}
                        onChange={(e) => setFormData(prev => ({ ...prev, recheck_required: e.target.checked }))}
                        className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                        data-testid="dbs-recheck-required"
                      />
                      <span className="text-slate-700 text-xs">Recheck required</span>
                    </label>
                  </div>
                </div>
                
                {/* Information Present Warning */}
                {formData.information_present && (
                  <div className="p-2 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-xs text-amber-700">
                      <AlertTriangle className="h-3 w-3 inline mr-1" />
                      Information/disclosures present on certificate. Include details in notes for risk assessment.
                    </p>
                  </div>
                )}
                
                {/* Result Summary */}
                <div className="space-y-1">
                  <Label className="text-xs">Result Summary</Label>
                  <Input
                    value={formData.result_summary}
                    onChange={(e) => setFormData(prev => ({ ...prev, result_summary: e.target.value }))}
                    placeholder="e.g., Clear - no information disclosed"
                    className="h-8 text-sm rounded-lg"
                    data-testid="dbs-result-summary"
                  />
                </div>
              </div>
              
              {/* Policy reminder */}
              <div className="p-2 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-xs text-blue-700">
                  <Info className="h-3 w-3 inline mr-1" />
                  DBS certificates have no statutory expiry. Set a policy-based recheck date (typically 3 years).
                </p>
              </div>
            </div>
          )}

          {/* Identity: completing a formally pending check is distinct from stamping evidence. */}
          {isIdentity && (
            <section className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="identity-formal-review-fields">
              <h4 className="text-sm font-semibold text-slate-900">Formal Identity Check</h4>
              <p className="text-xs text-slate-700">
                Compare the original identity evidence to the applicant. Saving Verified requires
                document type, full name, date of birth and explicit matching decisions.
                A previous document stamp does not automatically confirm these facts.
              </p>
              <div className="space-y-1">
                <Label>Identity document type *</Label>
                <Select value={formData.id_document_type}
                  onValueChange={value => setFormData(prev => ({ ...prev, id_document_type: value }))}>
                  <SelectTrigger data-testid="identity-document-type"><SelectValue placeholder="Choose document type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="passport">Passport</SelectItem>
                    <SelectItem value="driving_licence">Driving licence</SelectItem>
                    <SelectItem value="national_id">National identity card</SelectItem>
                    <SelectItem value="brp">Biometric residence permit</SelectItem>
                    <SelectItem value="birth_certificate">Birth certificate (no photo)</SelectItem>
                    <SelectItem value="adoption_certificate">Adoption certificate (no photo)</SelectItem>
                    <SelectItem value="other">Other ID (document reason required in notes)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label htmlFor="identity-full-name">Full name shown on evidence *</Label>
                  <Input id="identity-full-name" data-testid="identity-full-name"
                    value={formData.id_full_name_on_document}
                    onChange={e => setFormData(prev => ({ ...prev, id_full_name_on_document: e.target.value }))} />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="identity-dob">Date of birth shown on evidence *</Label>
                  <Input id="identity-dob" type="date" data-testid="identity-date-of-birth"
                    value={formData.id_date_of_birth}
                    onChange={e => setFormData(prev => ({ ...prev, id_date_of_birth: e.target.value }))} />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="identity-doc-number">Document number (when shown)</Label>
                  <Input id="identity-doc-number" value={formData.id_document_number}
                    onChange={e => setFormData(prev => ({ ...prev, id_document_number: e.target.value }))} />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="identity-expiry">Document expiry (if applicable)</Label>
                  <Input id="identity-expiry" type="date" value={formData.id_expiry_date}
                    onChange={e => setFormData(prev => ({ ...prev, id_expiry_date: e.target.value }))} />
                </div>
              </div>
              <div className="space-y-2 border-t border-slate-200 pt-3">
                {[
                  ['id_name_matches_application', 'I checked that the name matches the applicant'],
                  ['id_dob_matches_application', 'I checked that the date of birth matches the applicant'],
                  ...(['birth_certificate', 'adoption_certificate'].includes(formData.id_document_type)
                    ? [] : [['id_photo_match_confirmed', 'I checked that the photo matches the applicant']]),
                ].map(([field, label]) => (
                  <label key={field} className="flex cursor-pointer items-start gap-2 text-xs text-slate-800">
                    <input type="checkbox" checked={formData[field] === true}
                      onChange={e => setFormData(prev => ({ ...prev, [field]: e.target.checked }))}
                      data-testid={field} className="mt-0.5 h-4 w-4 rounded border-slate-300" />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
              {formData.outcome !== 'verified' && (
                <p className="text-xs text-amber-800">This check will remain pending until an authorised manager explicitly selects Verified and saves complete evidence-backed details.</p>
              )}
            </section>
          )}

          {/* Address: preserve source-verified documents, never fabricate their count. */}
          {isAddress && (
            <section className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="address-formal-review-fields">
              <h4 className="text-sm font-semibold text-slate-900">Formal Proof of Address Check</h4>
              <p className="text-xs text-slate-700">
                Reviewed address documents linked to this check: {(formData.address_verified_documents || []).length}
                {' '}of {formData.address_documents_required_count || 2} required.
                Complete the individual document reviews first if fewer than two are recorded.
              </p>
              <label className="flex cursor-pointer items-start gap-2 text-xs text-slate-800">
                <input type="checkbox" checked={formData.address_matches_application === true}
                  onChange={e => setFormData(prev => ({ ...prev, address_matches_application: e.target.checked }))}
                  data-testid="address-matches-applicant" className="mt-0.5 h-4 w-4 rounded border-slate-300" />
                <span>I confirmed both reviewed documents are valid and the address matches the applicant's record.</span>
              </label>
              <p className="text-xs text-amber-800">
                Selecting Verified does not bypass source evidence, date or tenancy checks.
                This form does not create or mark address documents reviewed.
              </p>
            </section>
          )}

          {/* Notes */}
          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Any additional notes about this check..."
              className="rounded-lg min-h-[80px]"
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleClose} className="rounded-xl">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || isUploading || isExtracting || !formData.method || (proofUploadRequired && !hasProof)}
            className="bg-primary hover:bg-primary-hover text-white rounded-xl"
            data-testid="record-check-submit"
          >
            {isSubmitting || isUploading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {isUploading ? 'Uploading...' : 'Saving...'}
              </>
            ) : (
              <>
                <Shield className="h-4 w-4 mr-2" />
                {existingCheck?.id ? 'Save Reviewed Check' : 'Record Check'}
              </>
            )}
          </Button>
        </DialogFooter>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

